import { useEffect, useMemo, useState } from 'react';
import {
  UserSettingsAppearanceCard,
  UserSettingsAuthProviders,
} from '@backstage/plugin-user-settings';
import {
  MinimalCard,
  CardType,
  SettingsUserIdentityCard,
} from '@internal/backstage-plugin-hm-react';
import {
  useApi,
  identityApiRef,
  configApiRef,
} from '@backstage/core-plugin-api';
import { Flex, Grid } from '@backstage/ui';
import { RiShieldUserFill, RiToolsFill, RiRobot2Fill } from '@remixicon/react';

export const IdentitySettings = () => {
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const rbacSuperUsers = useMemo(
    () =>
      (
        configApi.getOptionalConfigArray('permission.rbac.admin.superUsers') ??
        []
      ).map(entry => entry.getString('name')),
    [configApi],
  );

  const [isAdminGroup, setIsAdminGroup] = useState(false);

  useEffect(() => {
    const fetchIdentity = async () => {
      setIsAdminGroup(false);
      const identity = await identityApi.getBackstageIdentity();
      if (
        rbacSuperUsers.length > 0 &&
        identity.ownershipEntityRefs?.some((ref: string) =>
          rbacSuperUsers.includes(ref),
        )
      ) {
        setIsAdminGroup(true);
      } else {
        setIsAdminGroup(false);
      }
    };

    fetchIdentity();
  }, [identityApi, rbacSuperUsers]);

  return (
    <Flex direction="column" gap="4">
      {isAdminGroup ? (
        <Grid.Root
          columns={{ initial: '1', xs: '2', md: '4', lg: '6' }}
          gap="4"
        >
          <Grid.Item>
            <MinimalCard
              title="RBAC Management"
              type={CardType.tool}
              description="Configure RBAC Policies"
              actionRoute="/rbac"
              Icon={RiShieldUserFill}
            />
          </Grid.Item>

          <Grid.Item>
            <MinimalCard
              title="DevTools"
              type={CardType.tool}
              description="Backstage Config and Status"
              actionRoute="/devtools"
              Icon={RiToolsFill}
            />
          </Grid.Item>

          <Grid.Item>
            <MinimalCard
              title="Adoption Analytics"
              type={CardType.service}
              description="Backstage Adoption Analytics"
              actionRoute="/adoption-analytics"
              Icon={RiToolsFill}
            />
          </Grid.Item>

          <Grid.Item>
            <MinimalCard
              title="BOB"
              type={CardType.tool}
              description="Bulk operations bot for catalog-info.yaml"
              actionRoute="/bob/cost-code"
              Icon={RiRobot2Fill}
            />
          </Grid.Item>

          <Grid.Item>
            <MinimalCard
              title="Frontend Extension Explorer"
              type={CardType.tool}
              description="Inspect all New Frontend System extensions loaded in this app"
              actionRoute="/frontend-extensions-explorer"
              Icon={RiToolsFill}
            />
          </Grid.Item>

          <Grid.Item>
            <MinimalCard
              title="Visualizer"
              type={CardType.tool}
              description="Backstage Visualizer"
              actionRoute="/visualizer/text"
              Icon={RiToolsFill}
            />
          </Grid.Item>
        </Grid.Root>
      ) : null}

      <Grid.Root columns={{ initial: '1', lg: '2' }} gap="4">
        <Grid.Item>
          <SettingsUserIdentityCard />
        </Grid.Item>
        <Grid.Item>
          <UserSettingsAppearanceCard />
        </Grid.Item>
      </Grid.Root>

      <UserSettingsAuthProviders />
    </Flex>
  );
};
